import { FunctionComponent } from "react";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { CampaignEditor } from "../../../providers/CampaignSocketContext";

/** How many avatars are drawn before the rest collapse into a "+N more". */
const VISIBLE_AVATARS = 5;

export interface CampaignEditorsStripProps {
	editors: CampaignEditor[];
}

/** "AW" from "Andreas Wawer" — first letters of the first two words, "?" when there is no name. */
const initialsOf = (userName: string): string =>
	userName
		.split(" ")
		.map((part) => part[0])
		.join("")
		.toUpperCase()
		.slice(0, 2) || "?";

/**
 * Who else has this campaign open, from the campaign socket room. Renders nothing when the user is
 * alone, which is the usual case.
 */
export const CampaignEditorsStrip: FunctionComponent<CampaignEditorsStripProps> = ({ editors }) => {
	if (editors.length === 0) {
		return <></>;
	}

	const overflow = editors.slice(VISIBLE_AVATARS);

	return (
		<div
			className="flex flex-row items-center gap-2 py-1 pl-1 pr-2.5 bg-brand-white border border-border-light rounded-full"
			data-testid="campaign-editors-strip"
		>
			<div className="flex flex-row -space-x-2">
				{editors.slice(0, VISIBLE_AVATARS).map((editor) => (
					<Tooltip
						key={editor.socketId}
						content={
							<div>
								<p className="font-semibold">{editor.userName}</p>
								<p className="text-xs text-gray-500">
									Editing since {new Date(editor.joinedAt).toLocaleTimeString()}
								</p>
							</div>
						}
						direction="bottom"
					>
						<div className="w-7 h-7 text-xxs text-center text-white rounded-full bg-brand-light leading-[24px] border-2 border-white cursor-default">
							{initialsOf(editor.userName)}
						</div>
					</Tooltip>
				))}
			</div>
			{overflow.length > 0 && (
				<Tooltip
					content={
						<div>
							{overflow.map((editor) => (
								<p key={editor.socketId}>{editor.userName}</p>
							))}
						</div>
					}
					direction="bottom"
				>
					<span className="text-xs text-gray-500">+{overflow.length} more</span>
				</Tooltip>
			)}
			<span className="text-xs text-gray-500">editing</span>
		</div>
	);
};
